import React, {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  Dispatch,
  useEffect,
  useState,
  useCallback,
} from 'react';

// Interfaces
import { ChatHistory, Conversation } from '@Models/types';
import { useUtilityContext } from './prompt-toolbox/UtilityContext';
import { useAuth } from './CognitoAuthProvider';
import { aiChatAPI } from '@Lib/axios/ai-chat';

// Action types
type ChatHistoryAction =
  | { type: 'ADD_CHAT_HISTORY'; payload: ChatHistory }
  | { type: 'REMOVE_CHAT_HISTORY'; payload: { id: string } }
  | {
      type: 'ADD_CONVERSATION';
      payload: { chatId: string | null; conversation: Conversation };
    }
  | {
      type: 'REPLACE_CONVERSATION';
      payload: { chatId: string | null; conversation: Conversation[] };
    }
  | { type: 'SET_CHAT_HISTORIES'; payload: ChatHistory[] }
  | { type: 'SET_CURRENT_CHAT'; payload: { id: string | null } }
  | {
      type: 'UPDATE_CHAT_TITLE';
      payload: { chatId: string; newTitle: string };
    };

// Initial state
interface ChatHistoryState {
  chatHistories: ChatHistory[];
  fetchChatHistories: () => Promise<void>;
  fetchChatMessages: (chatId: string) => Promise<void>;
  currentChatId: string | null;
}

const initialChatHistoryState: ChatHistoryState = {
  chatHistories: [],
  currentChatId: null,
  fetchChatHistories: async () => {},
  fetchChatMessages: async () => {},
};

// Reducer
const chatHistoryReducer = (
  state: ChatHistoryState,
  action: ChatHistoryAction
): ChatHistoryState => {
  const sortChatHistories = (histories: ChatHistory[]) => {
    return histories.sort((a, b) => b.timestamp - a.timestamp);
  };

  switch (action.type) {
    case 'ADD_CHAT_HISTORY':
      return {
        ...state,
        chatHistories: sortChatHistories([
          ...state.chatHistories,
          action.payload,
        ]),
      };

    case 'REMOVE_CHAT_HISTORY':
      return {
        ...state,
        chatHistories: sortChatHistories(
          state.chatHistories.filter((chat) => chat.id !== action.payload.id)
        ),
      };

    case 'ADD_CONVERSATION':
      return {
        ...state,
        chatHistories: state.chatHistories.map((chat) =>
          chat.id === action.payload.chatId
            ? {
                ...chat,
                messages: [...chat.messages, action.payload.conversation],
                last_modified: new Date().toISOString(),
              }
            : chat
        ),
      };
    case 'REPLACE_CONVERSATION':
      return {
        ...state,
        chatHistories: state.chatHistories.map((chat) =>
          chat.id === action.payload.chatId
            ? {
                ...chat,
                messages: action.payload.conversation,
              }
            : chat
        ),
      };

    case 'UPDATE_CHAT_TITLE':
      return {
        ...state,
        chatHistories: state.chatHistories.map((chat) =>
          chat.id === action.payload.chatId
            ? {
                ...chat,
                title: action.payload.newTitle,
                last_modified: new Date().toISOString(),
              }
            : chat
        ),
      };

    case 'SET_CHAT_HISTORIES':
      return { ...state, chatHistories: action.payload };

    case 'SET_CURRENT_CHAT':
      return { ...state, currentChatId: action.payload.id };

    default:
      return state;
  }
};
interface AITokenUsage {
  totalCompletionTokens: number;
  latestPromptTokens: number;
  totalTokens: number;
}
// Context
interface ChatHistoryContextProps {
  state: ChatHistoryState;
  dispatch: Dispatch<ChatHistoryAction>;
  fetchChatHistories: () => Promise<void>;
  fetchChatMessages: (chatId: string) => Promise<void>;
  fetchAITokenUsage: (chatId: string) => Promise<AITokenUsage | undefined>;
  renameIndex: number | null;
  status: string;
  renameChat: (chatId: string, title: string) => Promise<void>;
}

const ChatHistoryContext = createContext<ChatHistoryContextProps | undefined>(
  undefined
);

// Provider
interface ChatHistoryProviderProps {
  children: ReactNode;
}

export const ChatHistoryProvider: React.FC<ChatHistoryProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    chatHistoryReducer,
    initialChatHistoryState
  );
  const { setLoader } = useUtilityContext();
  const [status, setStatus] = useState('');
  const [renameIndex, setRenameIndex] = useState<number | null>(null);

  const { logout } = useAuth();
  const aiChat = aiChatAPI(logout);

  const fetchChatHistories = useCallback(async () => {
    setLoader('chatHistory', true);
    setStatus('Fetching Chat History...');
    try {
      const data = await aiChat.getChatHistory();
      if (data.length !== 0) {
        dispatch({ type: 'SET_CHAT_HISTORIES', payload: data });
        // dispatch({ type: 'SET_CURRENT_CHAT', payload: { id: data[0].id } });
      }
      setStatus('Chat History fetched successfully');
    } catch (error) {
      console.error('Fetch Error[ChatHistory]:', error);
      setStatus('Failed to fetch chat history');
    } finally {
      setLoader('chatHistory', false);
    }
  }, []);

  const fetchChatMessages = useCallback(async (chatId: string) => {
    setLoader('chatMessages', true);
    try {
      const [data] = await aiChat.getChatMessages(chatId);
      if (data.messages.length !== 0) {
        dispatch({
          type: 'REPLACE_CONVERSATION',
          payload: { chatId: chatId, conversation: data.messages },
        });
      }
    } catch (error) {
      console.error('Fetch Error[ChatMessages]:', error);
    } finally {
      setLoader('chatMessages', false);
    }
  }, []);

  const fetchAITokenUsage = useCallback(async (chatId: string) => {
    try {
      const data: AITokenUsage = await aiChat.getAITokenUsage(chatId);
      return data;
    } catch (error) {
      console.error('Fetch Error[AITokenUsage]:', error);
    }
  }, []);

  const renameChat = async (chatId: string, title: string) => {
    setLoader('renameChat', true);
    const renameIndex = state.chatHistories.findIndex(
      (chat) => chat.id == chatId
    );
    setRenameIndex(renameIndex);
    setStatus('Renaming chat...');
    try {
      await aiChat.renameChatHistory(title, chatId);
      dispatch({
        type: 'UPDATE_CHAT_TITLE',
        payload: { chatId, newTitle: title },
      });
      setStatus('Chat renamed successfully');
    } catch (error) {
      setStatus('Failed to rename chat');
    } finally {
      setLoader('renameChat', false);
      setRenameIndex(null);
    }
    console.log(status);
  };

  useEffect(() => {
    fetchChatHistories();
  }, [fetchChatHistories]);

  return (
    <ChatHistoryContext.Provider
      value={{
        state,
        dispatch,
        fetchChatHistories,
        fetchChatMessages,
        fetchAITokenUsage,
        renameIndex,
        status,
        renameChat,
      }}
    >
      {children}
    </ChatHistoryContext.Provider>
  );
};

// Custom hook
export const useChatHistory = (): ChatHistoryContextProps => {
  const context = useContext(ChatHistoryContext);
  if (!context) {
    throw new Error('useChatHistory must be used within a ChatHistoryProvider');
  }
  return context;
};
